/**
 * BulkPublishProgress
 * Shows live progress and per-item results during bulk canvas publish.
 */
import { useState } from "react";
import {
  CheckCircle, XCircle, AlertCircle, Loader2, ExternalLink, RotateCcw, X,
} from "lucide-react";

function StatusIcon({ status }) {
  if (status === "success") return <CheckCircle size={13} className="text-green-400 shrink-0" />;
  if (status === "failed")  return <XCircle     size={13} className="text-red-400 shrink-0"   />;
  if (status === "skipped") return <AlertCircle size={13} className="text-yellow-400 shrink-0" />;
  return <Loader2 size={13} className="text-white/30 animate-spin shrink-0" />;
}

export default function BulkPublishProgress({ results, processing, total, onRetryFailed, onClose }) {
  const [expanded, setExpanded] = useState(null);

  const done    = results.length;
  const success = results.filter((r) => r.status === "success").length;
  const failed  = results.filter((r) => r.status === "failed").length;
  const skipped = results.filter((r) => r.status === "skipped").length;
  const pct     = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="border border-[#CBA052]/30 bg-[#0a0a0a] rounded mb-5" data-testid="bulk-publish-progress">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b border-white/[0.06]">
        {processing ? (
          <Loader2 size={14} className="text-[#CBA052] animate-spin shrink-0" />
        ) : (
          <CheckCircle size={14} className="text-[#CBA052] shrink-0" />
        )}
        <span className="text-xs font-bold text-[#CBA052] uppercase tracking-wider">
          {processing ? "Publishing to Shopify..." : "Bulk Publish Complete"}
        </span>
        <span className="text-[10px] text-white/30 ml-1">{done} / {total}</span>
        {!processing && (
          <button onClick={onClose} className="ml-auto text-white/30 hover:text-white" data-testid="bulk-publish-close">
            <X size={14} />
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Progress bar */}
        <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#CBA052] transition-all duration-300 rounded-full"
            style={{ width: `${pct}%` }}
            data-testid="bulk-publish-progress-bar"
          />
        </div>

        {/* Summary counters */}
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="bg-white/[0.03] rounded p-2">
            <p className="text-sm font-bold text-white">{total}</p>
            <p className="text-[9px] text-white/40 uppercase">Total</p>
          </div>
          <div className="bg-green-500/[0.06] rounded p-2">
            <p className="text-sm font-bold text-green-400">{success}</p>
            <p className="text-[9px] text-green-400/60 uppercase">Success</p>
          </div>
          <div className="bg-red-500/[0.06] rounded p-2">
            <p className="text-sm font-bold text-red-400">{failed}</p>
            <p className="text-[9px] text-red-400/60 uppercase">Failed</p>
          </div>
          <div className="bg-yellow-500/[0.06] rounded p-2">
            <p className="text-sm font-bold text-yellow-400">{skipped}</p>
            <p className="text-[9px] text-yellow-400/60 uppercase">Skipped</p>
          </div>
        </div>

        {/* Per-item results */}
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {results.map((r, i) => (
            <div key={r.product_id || i}>
              <button
                onClick={() => setExpanded(expanded === i ? null : i)}
                className="w-full flex items-center gap-2.5 py-1.5 px-2 rounded text-left hover:bg-white/[0.03] transition-colors"
                data-testid={`bulk-result-${r.product_id}`}
              >
                <StatusIcon status={r.status} />
                <span className="text-[11px] font-medium text-white truncate flex-1">{r.title || r.product_id}</span>
                <span className={`text-[9px] uppercase font-bold shrink-0 ${
                  r.status === "success" ? "text-green-400" : r.status === "failed" ? "text-red-400" : "text-yellow-400"
                }`}>
                  {r.status}
                </span>
                {r.shopify_product_url && (
                  <a
                    href={r.shopify_product_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-white/25 hover:text-[#CBA052] shrink-0"
                  >
                    <ExternalLink size={10} />
                  </a>
                )}
              </button>
              {expanded === i && (r.error || r.reason) && (
                <div className="ml-6 mb-1 px-2 py-1.5 rounded bg-white/[0.02] border border-white/[0.04]">
                  <p className="text-[10px] text-white/40 leading-relaxed">{r.error || r.reason}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Retry failed */}
        {!processing && failed > 0 && (
          <button
            onClick={onRetryFailed}
            className="flex items-center gap-1.5 text-xs font-bold text-[#CBA052] border border-[#CBA052]/30 px-4 py-2 rounded hover:bg-[#CBA052]/10 transition-colors"
            data-testid="bulk-publish-retry"
          >
            <RotateCcw size={12} /> Retry {failed} Failed
          </button>
        )}
      </div>
    </div>
  );
}
